import { routes } from "@bezoned/shared/routes.js";

import { updateSearchParams } from "../../utils/url-search-params.js";

export const WAIT_FOR_RESPONSE_FROM_MICROSOFT_MS = 10_000;
export const ADMIN_CONSENT_FLOW_VALUES = [
  "onboarding",
  "permission-upgrade",
] as const;

export type AdminConsentFlow = (typeof ADMIN_CONSENT_FLOW_VALUES)[number];

const ADMIN_CONSENT_SEARCH_STORAGE_KEY = "bezoned:admin-consent-search";

const getMarketingSearch = (search: string) => {
  const searchParams = new URLSearchParams(search);

  for (const key of [...searchParams.keys()]) {
    if (key !== "stage" && !key.toLowerCase().startsWith("utm_")) {
      searchParams.delete(key);
    }
  }

  const nextSearch = searchParams.toString();
  return nextSearch ? `?${nextSearch}` : "";
};

const getAdminConsentSearchStorageKey = (flow: AdminConsentFlow) =>
  `${ADMIN_CONSENT_SEARCH_STORAGE_KEY}:${flow}`;

export const rememberAdminConsentSearch = (
  flow: AdminConsentFlow,
  search: string
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      getAdminConsentSearchStorageKey(flow),
      getMarketingSearch(search)
    );
  } catch {
    // Ignore storage failures; consent must still be allowed to continue.
  }
};

export const getRememberedAdminConsentSearch = (flow: AdminConsentFlow) => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return (
      window.sessionStorage.getItem(getAdminConsentSearchStorageKey(flow)) ?? ""
    );
  } catch {
    return "";
  }
};

export const clearRememberedAdminConsentSearch = (flow: AdminConsentFlow) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(getAdminConsentSearchStorageKey(flow));
  } catch {
    // Ignore storage failures; consent has already completed.
  }
};

export const buildEnsureTenantUrl = (
  ensureTenantPath: string,
  params: Record<string, string | undefined>,
  search = ""
) => `${ensureTenantPath}${updateSearchParams(search, { set: params })}`;

const ADMIN_CONSENT_ROUTE_CONFIG = {
  onboarding: {
    endPath: routes.public.auth.adminConsent.onboardingEnd,
    errorRedirectParams: { error: "true", step: "permissions" },
    errorRedirectPath: routes.protected.chooseTeam,
    startPath: routes.public.auth.adminConsent.onboardingStart,
    successRedirectParams: { step: "create" },
    successRedirectPath: routes.protected.chooseTeam,
  },
  "permission-upgrade": {
    endPath: routes.public.auth.adminConsent.permissionUpgradeEnd,
    errorRedirectParams: { error: "true" },
    errorRedirectPath: routes.protected.permissionUpgrade,
    startPath: routes.public.auth.adminConsent.permissionUpgradeStart,
    successRedirectParams: { granted: "true" },
    successRedirectPath: routes.protected.permissionUpgrade,
  },
} satisfies Record<
  AdminConsentFlow,
  {
    startPath: string;
    endPath: string;
    successRedirectPath: string;
    successRedirectParams: Record<string, string | undefined>;
    errorRedirectPath: string;
    errorRedirectParams: Record<string, string | undefined>;
  }
>;

export const buildAdminConsentStartPath = ({
  flow,
  tenantId,
}: {
  flow: AdminConsentFlow;
  tenantId: string;
}) =>
  ADMIN_CONSENT_ROUTE_CONFIG[flow].startPath.replace(
    ":tenantId",
    encodeURIComponent(tenantId)
  );

export const buildAdminConsentStartUrl = ({
  flow,
  tenantId,
  frontendUrl,
  search = "",
}: {
  flow: AdminConsentFlow;
  tenantId: string;
  frontendUrl: string;
  search?: string;
}) =>
  `${frontendUrl}${buildAdminConsentStartPath({ flow, tenantId })}${search}`;

export const getAdminConsentEndPath = (flow: AdminConsentFlow) =>
  ADMIN_CONSENT_ROUTE_CONFIG[flow].endPath;

export const getAdminConsentSuccessRedirectPath = (
  flow: AdminConsentFlow,
  search = ""
) =>
  buildEnsureTenantUrl(
    ADMIN_CONSENT_ROUTE_CONFIG[flow].successRedirectPath,
    ADMIN_CONSENT_ROUTE_CONFIG[flow].successRedirectParams,
    search
  );

export const getAdminConsentErrorRedirectPath = (
  flow: AdminConsentFlow,
  search = "",
  consentOutcome?: "denied" | "error"
) =>
  buildEnsureTenantUrl(
    ADMIN_CONSENT_ROUTE_CONFIG[flow].errorRedirectPath,
    {
      ...ADMIN_CONSENT_ROUTE_CONFIG[flow].errorRedirectParams,
      ...(flow === "onboarding" && consentOutcome ? { consentOutcome } : {}),
    },
    search
  );

export const buildAdminConsentUrl = ({
  tenantId,
  clientId,
  frontendUrl,
  adminConsentEndPath,
  loginHint,
}: {
  tenantId: string;
  clientId: string;
  frontendUrl: string;
  adminConsentEndPath: string;
  loginHint?: string;
}) => {
  const targetUrl = `${frontendUrl}${adminConsentEndPath}`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: targetUrl,
    scope: `${clientId}/.default`,
  });

  if (loginHint) {
    params.set("login_hint", loginHint);
  }
  // TODO(auth): Add backend-minted state correlation for the admin consent flow.
  // The frontend should stop treating the Microsoft callback as self-authenticating
  // based only on `admin_consent=true` and the returned `tenant` query value.
  //
  // Intended design:
  // - The backend should mint an opaque or signed `state` value before redirecting
  //   to Microsoft.
  // - That state should encode or reference the expected tenant, the consent flow
  //   (`onboarding` vs `permission-upgrade`), and for upgrades the target
  //   permission version approved by the backend.
  // - `buildAdminConsentUrl` should append that `state` query parameter to the
  //   Microsoft `/adminconsent` URL.
  // - The callback handling in this file should validate the returned `state`
  //   against backend expectations instead of trusting callback query params
  //   alone. In particular, do not rely on the returned `tenant` value as proof
  //   that the original request matches the current authenticated tenant.
  // - Only after backend state validation succeeds should the backend allow a
  //   permission-upgrade flow to advance the tenant's stored permission version.

  // See: https://learn.microsoft.com/en-us/entra/identity-platform/v2-admin-consent
  return `https://login.microsoftonline.com/${tenantId}/v2.0/adminconsent?${params.toString()}`;
};

interface ConsentResponseState {
  hasPermissions: boolean | null;
  permissionsError: string | null;
  permissionsGrantedForTenantId: string | null;
}

export const hasResponseFromMicrosoft = ({
  hasPermissions,
  permissionsError,
  permissionsGrantedForTenantId,
}: ConsentResponseState) =>
  hasPermissions !== null ||
  permissionsError !== null ||
  permissionsGrantedForTenantId !== null;

export const isAdminConsentSuccess = ({
  hasPermissions,
  permissionsError,
  permissionsGrantedForTenantId,
}: ConsentResponseState) =>
  hasPermissions === true &&
  permissionsError === null &&
  permissionsGrantedForTenantId !== null;
